/**
 * @function TestsTab
 * @description Component for displaying and managing a participant's tests.
 * @returns {JSX.Element | null} - The TestsTab component.
 */

import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { useOutletContext } from 'react-router-dom'
import TestsIcon from '../../../assets/tests.svg'
import { useModal } from '../../../hooks/useModal'
import {
    useGetAssignmentsByParticipantIdQuery,
    useGetAssignmentsCountByParticipantIdQuery,
} from '../../../services/assignments'
import { formatDuration } from '../../../utils/formatDuration'
import { formatOutput } from '../../../utils/formatOutput'
import { formatScore } from '../../../utils/formatScore'
import { formatTestStatus } from '../../../utils/formatTestStatus'
import { AssignTestButton } from '../../AssignTestButton'
import { ShareAssignment } from '../../modals/content/ShareAssignment'
import { Pagination } from '../../Pagination'

const columns = [
    'Battery and AID',
    'Status',
    'Score',
    'QC',
    'Duration',
    'Device',
    'Assigned',
    'Last active',
    'Completed',
]

/**
 * @function classNames
 * @description Combines class names into a single string.
 * @param {...string} classes - The class names to combine.
 * @returns {string} - The combined class names.
 */
function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ')
}

/**
 * @function formatQC
 * @description Formats the QC status of a test.
 * @param {boolean} qc - The QC status of the test.
 * @returns {JSX.Element} - The formatted QC status.
 */
function formatQC(qc: boolean) {
    if (qc === undefined) {
        return formatOutput(qc)
    }

    return (
        <span
            className={classNames(
                'whitespace-nowrap text-xs font-medium rounded-[10px] py-0.5 px-2',
                !qc ? 'bg-red-100 text-red-800' : 'bg-indigo-100 text-indigo-800'
            )}
        >
            {qc ? 'Passed' : 'Failed'}
        </span>
    )
}

/**
 * @function formatDate
 * @description Formats a date string into a readable format.
 * @param {string} date - The date string to format.
 * @returns {string} - The formatted date string.
 */
function formatDate(date: string) {
    if (!date) {
        return formatOutput(date)
    }
    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC',
        hour: 'numeric',
        minute: 'numeric',
    }
    return formatOutput(new Intl.DateTimeFormat('en-US', options).format(new Date(date)))
}

/**
 * @function formatID
 * @description Formats an ID with a prefix.
 * @param {string} prefix - The prefix to format.
 * @param {string} id - The ID to format.
 * @returns {JSX.Element} - The formatted ID.
 */
function formatID(prefix: string, id: string) {
    return prefix + '–' + id.split('-')[0]
}

/**
 * @function TestsTab
 * @description Component for displaying and managing a participant's tests.
 * @returns {JSX.Element | null} - The TestsTab component.
 */
export function TestsTab() {
    const [open] = useModal()
    const { participant } = useOutletContext<{ participant: Novoic.API.Participant }>()
    const isCompleted = (status: string) => status === 'complete'
    const { data: assignments, isLoading } = useGetAssignmentsByParticipantIdQuery(participant.id)
    const { data: total } = useGetAssignmentsCountByParticipantIdQuery(participant.id)

    function onTestClick(test: Novoic.API.Assignment) {
        if (test.type === 'assignment_invitation') {
            open({
                content: (
                    <ShareAssignment
                        participantId={participant.id}
                        isPhoneVerified={
                            participant.is_phone_verified && participant.is_sms_permitted
                        }
                        isEmailVerified={participant.is_email_defined}
                        invitationId={test.id}
                    />
                ),
            })
        }
    }

    if (isLoading) {
        return null
    }

    if (total?.count === 0) {
        return (
            <div className="flex flex-col items-center">
                <div className="flex flex-col items-center mt-24">
                    <div className="w-16 h-16 bg-indigo-50 flex items-center justify-center rounded-full">
                        <TestsIcon />
                    </div>
                    <h2 className="mt-4 text-xl text-gray-900 font-semibold">
                        There are no tests to display
                    </h2>
                    <p className="mt-3 mb-6 text-gray-600 text-base">
                        Assign new test by pressing the blue button below
                    </p>

                    <AssignTestButton
                        studyId={participant.studies[0]?.study_id}
                        participantId={participant.id}
                    />
                </div>
            </div>
        )
    }

    return (
        <>
            <div className="px-8 py-8">
                <AssignTestButton
                    studyId={participant.studies[0]?.study_id}
                    participantId={participant.id}
                />
            </div>
            <table>
                <thead className="border-b">
                    <tr>
                        {columns.map((col) => (
                            <th
                                key={col}
                                scope="col"
                                className="py-2.5 first:pl-8 pl-3 text-left text-sm font-medium text-gray-900 whitespace-nowrap"
                            >
                                <button className="group inline-flex">
                                    {col}
                                    <span className="invisible ml-2 flex-none rounded text-gray-400 group-hover:visible group-focus:visible">
                                        <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
                                    </span>
                                </button>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                    {assignments.map((assignment: Novoic.API.Assignment) => (
                        <tr
                            key={assignment.id}
                            onClick={() => onTestClick(assignment)}
                            className={`${
                                assignment.type === 'assignment_invitation'
                                    ? 'hover:bg-[#f7f9ff] cursor-pointer transition-all'
                                    : ''
                            }`}
                        >
                            <td className="pl-8 py-4 text-sm" role="presentation">
                                <div>
                                    <span className="font-medium text-gray-900">
                                        {formatOutput(assignment.battery?.name)}
                                    </span>
                                    <p className="text-xs text-gray-600">
                                        {assignment.type === 'assignment'
                                            ? formatID('AID', assignment.id)
                                            : null}
                                    </p>
                                </div>
                            </td>
                            <td className="px-3 py-4 text-sm text-gray-600">
                                {formatTestStatus(assignment.status)}
                            </td>
                            <td className="px-3 py-4 text-sm text-gray-600">
                                {isCompleted(assignment.status)
                                    ? formatScore(assignment.score, assignment.level)
                                    : formatOutput(null)}
                            </td>
                            <td className="px-3 py-4 text-sm text-gray-600">
                                {formatQC(assignment.qc_pass)}
                            </td>
                            <td className="px-3 py-4 text-sm text-gray-600">
                                {formatDuration(assignment.fact_duration)}
                            </td>
                            <td className="px-3 py-4 text-sm text-gray-600">
                                {formatOutput(assignment.device_type)}
                            </td>
                            <td className="px-3 py-4 text-sm text-gray-600">
                                {formatDate(
                                    assignment.type === 'assignment'
                                        ? assignment.start_time
                                        : assignment.created_at
                                )}
                            </td>
                            <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-600">
                                {formatDate(assignment.last_question_time)}
                            </td>
                            <td className="px-3 py-4 text-sm text-gray-600">
                                {assignment.status !== 'incomplete'
                                    ? formatDate(assignment.fact_end_time)
                                    : formatOutput(undefined)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className="flex w-full h-[74px] items-center bg-white border-t border-t-gray-200">
                <Pagination total={total?.count} />
            </div>
        </>
    )
}
